import type { StructuredContentRoot } from "@repo/app-types/content/structured/types";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type FC,
  type FocusEvent,
  type MouseEvent,
  type RefObject,
  type TransitionEvent,
} from "react";
import { twMerge } from "tailwind-merge";
import {
  renderStructuredContent,
  type StructuredContentRenderContext,
} from "../structuredContent/structuredContentRenderer.js";

const HEIGHT_TRANSITION_DURATION_MS = 250;
const COLLAPSE_MEASUREMENT_FALLBACK_BUFFER_MS = 50;

export interface CoachingGoalBodyPreviewProps {
  /** The coaching note body (V3 AST); headings render as section labels. */
  content: StructuredContentRoot;
  /** Render context so grading references/quotes become interactive. */
  renderContext?: StructuredContentRenderContext;
  isExpanded: boolean;
  /** Keeps the body expanded and hides the collapse toggle (e.g. while the manager note editor is open). */
  isExpansionForced?: boolean;
  onExpandedChange: (isExpanded: boolean) => void;
}

export const CoachingGoalBodyPreview: FC<CoachingGoalBodyPreviewProps> = (
  props,
) => {
  const {
    content,
    renderContext,
    isExpanded,
    isExpansionForced = false,
    onExpandedChange,
  } = props;
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const gradientRef = useRef<HTMLDivElement | null>(null);
  const toggleButtonRef = useRef<HTMLButtonElement | null>(null);
  const hasRequestedAutoExpansionRef = useRef(false);
  const isCollapsingRef = useRef(false);
  const collapseMeasurementTimerRef = useRef<number | undefined>(undefined);
  const [isOverflowing, setIsOverflowing] = useState(true);

  const bodyId = useId();
  const showsExpandedBody = isExpanded || isExpansionForced;
  const showsExpandedBodyRef = useRef(showsExpandedBody);

  const measureOverflow = useCallback(() => {
    const wrapperElement = wrapperRef.current;
    const bodyElement = bodyRef.current;

    if (!wrapperElement || !bodyElement) {
      return;
    }

    const wrapperStyles = getComputedStyle(wrapperElement);
    const collapsedContentHeight =
      wrapperElement.clientHeight -
      Number.parseFloat(wrapperStyles.paddingTop) -
      Number.parseFloat(wrapperStyles.paddingBottom);

    setIsOverflowing(bodyElement.scrollHeight > collapsedContentHeight);
  }, []);

  const finishCollapseMeasurement = useCallback(() => {
    window.clearTimeout(collapseMeasurementTimerRef.current);
    isCollapsingRef.current = false;
    if (!showsExpandedBodyRef.current) {
      measureOverflow();
    }
  }, [measureOverflow]);

  useLayoutEffect(() => {
    const wasShowingExpandedBody = showsExpandedBodyRef.current;
    showsExpandedBodyRef.current = showsExpandedBody;

    if (showsExpandedBody) {
      window.clearTimeout(collapseMeasurementTimerRef.current);
      isCollapsingRef.current = false;
      return;
    }

    if (!wasShowingExpandedBody) {
      return;
    }

    // Every expanded -> collapsed transition can animate, including a parent
    // releasing `isExpansionForced`. Guard overflow measurement before the
    // ResizeObserver starts watching that transition; measuring its interim
    // heights can briefly hide and remount the disclosure on every frame.
    isCollapsingRef.current = true;
    if (wrapperRef.current) {
      wrapperRef.current.scrollTop = 0;
    }
    collapseMeasurementTimerRef.current = window.setTimeout(
      finishCollapseMeasurement,
      HEIGHT_TRANSITION_DURATION_MS + COLLAPSE_MEASUREMENT_FALLBACK_BUFFER_MS,
    );
  }, [finishCollapseMeasurement, showsExpandedBody]);

  useEffect(() => {
    const wrapperElement = wrapperRef.current;
    const bodyElement = bodyRef.current;

    if (!wrapperElement || !bodyElement || showsExpandedBody) {
      return;
    }

    const measureStableCollapsedOverflow = () => {
      if (!isCollapsingRef.current) {
        measureOverflow();
      }
    };

    // Measure against the real collapsed content box. The preview is flex-auto
    // so a grid-stretched card can make it taller than h-32; a fixed sentinel
    // would then report overflow even when every line is already visible.
    measureStableCollapsedOverflow();

    const observer = new ResizeObserver(measureStableCollapsedOverflow);
    observer.observe(wrapperElement);
    observer.observe(bodyElement);

    return () => {
      observer.disconnect();
    };
  }, [content, measureOverflow, showsExpandedBody]);

  useEffect(
    () => () => {
      window.clearTimeout(collapseMeasurementTimerRef.current);
    },
    [],
  );

  useEffect(() => {
    if (!showsExpandedBody) {
      hasRequestedAutoExpansionRef.current = false;
    }
  }, [content, showsExpandedBody]);

  const requestAutoExpansion = () => {
    if (
      showsExpandedBody ||
      !isOverflowing ||
      hasRequestedAutoExpansionRef.current
    ) {
      return;
    }

    hasRequestedAutoExpansionRef.current = true;
    // Keyboard focus and find-in-page can scroll an overflow-hidden ancestor
    // before their event reaches us. Clear that browser-created offset before
    // changing height so the first expansion frame starts at the note's top.
    if (wrapperRef.current) {
      wrapperRef.current.scrollTop = 0;
    }
    onExpandedChange(true);
  };

  const handleBodyFocusCapture = (event: FocusEvent<HTMLDivElement>) => {
    const wrapperElement = wrapperRef.current;
    const gradientElement = gradientRef.current;
    const focusTarget = event.target;

    if (
      showsExpandedBody ||
      !isOverflowing ||
      !wrapperElement ||
      !gradientElement ||
      !(focusTarget instanceof HTMLElement)
    ) {
      return;
    }

    const wrapperBounds = wrapperElement.getBoundingClientRect();
    const focusTargetBounds = focusTarget.getBoundingClientRect();
    // Focusing an overflow-hidden descendant can scroll the wrapper before the
    // focus event arrives. Add scrollTop back to recover the target's position
    // in the unscrolled content, then compare it with the start of the fade.
    const focusTargetBottom =
      focusTargetBounds.bottom - wrapperBounds.top + wrapperElement.scrollTop;
    // The preview reserves a paint gutter around its 128px content box so
    // focus rings remain inside the clipping edge. Compare against the
    // gradient's real position rather than deriving it from clientHeight,
    // which includes that gutter.
    const unobscuredContentBottom = gradientElement.offsetTop;

    if (focusTargetBottom > unobscuredContentBottom) {
      requestAutoExpansion();
    }
  };

  const handleWrapperScroll = () => {
    // overflow-hidden has no user scroll affordance. A non-zero offset while
    // collapsed therefore comes from browser behavior such as focusing a
    // clipped control or revealing a find-in-page match; reveal the full note.
    if ((wrapperRef.current?.scrollTop ?? 0) > 0) {
      requestAutoExpansion();
    }
  };

  const handleWrapperClick = (event: MouseEvent<HTMLDivElement>) => {
    const clickTarget = event.target;
    if (
      showsExpandedBody ||
      !isOverflowing ||
      !(clickTarget instanceof Element) ||
      clickTarget.closest(
        "a, button, input, select, textarea, [role='button'], [role='link']",
      )
    ) {
      return;
    }

    requestAutoExpansion();
  };

  const handleWrapperTransitionEnd = (
    event: TransitionEvent<HTMLDivElement>,
  ) => {
    if (
      event.target === event.currentTarget &&
      event.propertyName === "height"
    ) {
      finishCollapseMeasurement();
    }
  };

  const handleToggleClick = () => {
    if (showsExpandedBody) {
      // Safari can leave focus on a previously activated descendant when a
      // pointer click does not focus the disclosure button. Move focus to the
      // persistent control before clipping that descendant again.
      if (bodyRef.current?.contains(document.activeElement)) {
        toggleButtonRef.current?.focus();
      }
      // A browser-driven focus/find scroll can survive until the intrinsic-size
      // transition finishes. Reset it before shrinking so collapse always
      // returns to the beginning of the note and cannot immediately re-expand.
      if (wrapperRef.current) {
        wrapperRef.current.scrollTop = 0;
      }
      onExpandedChange(false);
      return;
    }

    onExpandedChange(true);
  };

  const showsToggle =
    !isExpansionForced && (isOverflowing || showsExpandedBody);

  const bodyContent = (
    <div
      id={bodyId}
      ref={bodyRef}
      className="flex-auto text-sm text-foreground"
      onFocusCapture={handleBodyFocusCapture}
    >
      {renderStructuredContent(content, renderContext ?? {})}
    </div>
  );

  return (
    <div
      ref={wrapperRef}
      data-coaching-goal-body-preview=""
      data-expanded={showsExpandedBody}
      className={twMerge(
        "group relative -m-2 box-content flex flex-auto flex-col overflow-hidden p-2 text-sm leading-6 text-foreground [interpolate-size:allow-keywords]",
        "motion-safe:supports-[interpolate-size:allow-keywords]:transition-[height] motion-safe:supports-[interpolate-size:allow-keywords]:duration-[250ms] motion-safe:supports-[interpolate-size:allow-keywords]:ease-[cubic-bezier(0.25,0.46,0.45,0.94)]",
        showsExpandedBody ? "h-auto" : "h-32",
        isOverflowing && !showsExpandedBody ? "cursor-pointer" : "",
      )}
      onClick={handleWrapperClick}
      onScroll={handleWrapperScroll}
      onTransitionEnd={handleWrapperTransitionEnd}
    >
      {bodyContent}

      {/* Reserve room for the bottom-anchored control only after expansion.
          Keeping this spacer out of the collapsed state preserves the existing
          scrollHeight > clientHeight overflow determination. */}
      {showsExpandedBody && showsToggle ? (
        <div aria-hidden="true" className="h-16 shrink-0" />
      ) : null}

      {showsToggle ? (
        <>
          <div
            ref={gradientRef}
            aria-hidden="true"
            data-coaching-goal-body-preview-gradient=""
            onClick={showsExpandedBody ? undefined : requestAutoExpansion}
            className={twMerge(
              "absolute inset-x-0 bottom-0 z-10 h-[88px] bg-gradient-to-b from-white/0 via-white/90 to-white",
              "motion-safe:supports-[interpolate-size:allow-keywords]:transition-opacity motion-safe:supports-[interpolate-size:allow-keywords]:duration-[120ms] motion-safe:supports-[interpolate-size:allow-keywords]:ease-out",
              showsExpandedBody
                ? "pointer-events-none opacity-0 motion-safe:supports-[interpolate-size:allow-keywords]:delay-[250ms]"
                : "cursor-pointer opacity-100 motion-safe:supports-[interpolate-size:allow-keywords]:delay-0",
            )}
          />
          <div className="pointer-events-none absolute inset-x-2 bottom-2 z-20 flex">
            <CoachingGoalDetailsToggleButton
              buttonRef={toggleButtonRef}
              controlsId={bodyId}
              isExpanded={showsExpandedBody}
              onClick={handleToggleClick}
            />
          </div>
        </>
      ) : null}
    </div>
  );
};

interface CoachingGoalDetailsToggleButtonProps {
  buttonRef: RefObject<HTMLButtonElement | null>;
  controlsId: string;
  isExpanded: boolean;
  onClick: () => void;
}

const CoachingGoalDetailsToggleButton: FC<
  CoachingGoalDetailsToggleButtonProps
> = (props) => {
  const { buttonRef, controlsId, isExpanded, onClick } = props;

  return (
    <button
      ref={buttonRef}
      type="button"
      aria-controls={controlsId}
      aria-expanded={isExpanded}
      onClick={(event) => {
        // The surrounding preview is also clickable. Keep this button's click
        // from invoking both handlers and reporting one state change twice.
        event.stopPropagation();
        onClick();
      }}
      className={twMerge(
        "pointer-events-auto flex h-8 w-full items-center justify-center gap-2 rounded-md border border-border-light bg-white px-3 text-xs font-semibold text-foreground transition-colors hover:bg-gray-50 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-indigo-600",
        !isExpanded ? "group-hover:bg-gray-50" : "",
      )}
    >
      {isExpanded ? (
        <IconChevronUp className="size-4" />
      ) : (
        <IconChevronDown className="size-4" />
      )}
      {isExpanded ? "Hide details" : "Continue reading"}
    </button>
  );
};
